import React from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";

import type { Task } from "types/Task";
import { TASKS_QUERY_KEY } from "services/dbApi/hooks/useTasksQuery";

/** Cache-only single-row writer, keyed by `jobId`. See {@link writeTaskToCache}. */
export type SetTaskFn = (
  jobId: Task["jobId"],
  updater: (prev: Task) => Task
) => void;

/**
 * Durable single-row writer, keyed by `jobId`: writes the cache *and* PATCHes.
 * The `commit*` half of the set/commit split (ADR 056). Declared here beside
 * {@link SetTaskFn} because the write helpers and the cache-ops factory that
 * implements it both need the type, and neither should import the other.
 */
export type CommitTaskFn = (
  jobId: Task["jobId"],
  fields: Partial<Task>
) => void;

/**
 * Result of a terminal-directive claim. `claimed` is the compare-and-set
 * outcome; `gone` is true when the row no longer exists server-side (404), in
 * which case callers must not re-PATCH it. Takes a `jobId`; the implementation
 * resolves it to the row PK the claim endpoint addresses.
 */
export type ClaimTerminal = (
  jobId: Task["jobId"]
) => Promise<{ claimed: boolean; gone?: boolean }>;

/**
 * Cache-only write to a single task row, keyed by `jobId`. The `set*` half of the
 * set/commit split (ADR 056): updates the tasks React Query cache in place and
 * never writes through to the backend. Durable transitions go through
 * `usePatchTaskMutation` directly.
 *
 * A write that changes nothing does not re-render `TASKS_QUERY_KEY` consumers:
 * React Query's structural sharing collapses a deep-equal list back to the
 * previous array reference. "Deep-equal" includes the key set and distinguishes
 * `null` from `undefined`, so an updater that writes an explicit `undefined` over
 * a field the cached row either lacks or holds as `null` does produce a new
 * reference; see the test of that case.
 */
export function writeTaskToCache(
  queryClient: QueryClient,
  jobId: Task["jobId"],
  updater: (prev: Task) => Task
): void {
  queryClient.setQueryData<Task[]>(TASKS_QUERY_KEY, (prev) => {
    if (!prev) return prev;
    return prev.map((task) => (task.jobId === jobId ? updater(task) : task));
  });
}

/** Returns a stable {@link writeTaskToCache} bound to the active query client. */
export function useTaskCache(): SetTaskFn {
  const queryClient = useQueryClient();
  return React.useCallback<SetTaskFn>(
    (jobId, updater) => writeTaskToCache(queryClient, jobId, updater),
    [queryClient]
  );
}
